import axios from "axios";

const normalizeUserData = (userData) => {
  if (!userData) {
    return null;
  }

  const identityData = userData.identityData || {};
  const normalizedFirstName =
    identityData.firstName || userData.firstName || userData.given_name || "";
  const normalizedLastName =
    identityData.lastName || userData.lastName || userData.family_name || "";
  const normalizedDisplayName =
    identityData.displayName ||
    userData.displayName ||
    [normalizedFirstName, normalizedLastName].filter(Boolean).join(" ") ||
    "";
  const normalizedProfilePicture =
    identityData.profilePicture ||
    userData.profilePicture ||
    userData.picture ||
    userData.photoURL ||
    "";

  return {
    ...userData,
    identityData: {
      ...identityData,
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      displayName: normalizedDisplayName,
      profilePicture: normalizedProfilePicture,
    },
  };
};

export default {
  namespaced: true,
  state: {
    userID: null,
    userData: null,
    isLoading: true,
    error: null,
    _promise: null,
    authChecked: false,
  },
  mutations: {
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setError(state, error) {
      state.error = error;
    },
    setUserData(state, userData) {
      const normalizedUserData = normalizeUserData(userData);
      state.userData = normalizedUserData;
      state.userID = normalizedUserData ? normalizedUserData._id : null;
    },
    clearUserData(state) {
      state.userData = null;
      state.userID = null;
    },
    setAuthChecked(state, val) {
      state.authChecked = val;
    },
  },
  actions: {
    async fetchCurrentUser({ state, commit }, options = {}) {
      const force = options.force === true;

      /* 1️⃣ already checked during this page-life? */
      if (state.authChecked && !force) return state.userData;

      /* 2️⃣ another call already in flight?  → just await it here */
      if (state._promise) {
        try {
          await state._promise; // swallow its own error handling
        } catch (_) {
          /* nothing – original promise handler already did the work */
        }
        return state.userData; // never expose the raw promise
      }

      /* 3️⃣ first call today */
      commit("setLoading", true);
      commit("setError", null);

      state._promise = axios.get(
        `${process.env.VUE_APP_DEPLOY_URL}/auth/current-user`,
        { withCredentials: true }
      );

      try {
        const { data } = await state._promise;

        if (data.isAuthenticated && data.user) {
          commit("setUserData", data.user);
        } else {
          commit("clearUserData");
        }
      } catch (err) {
        if (err.response?.status === 401) {
          commit("clearUserData");
          // router.push('/login')  // optional
        } else {
          commit("setError", err.message || "Failed to fetch user data");
        }
      } finally {
        commit("setLoading", false);
        commit("setAuthChecked", true); // mark that we tried once
        state._promise = null; // reset so a manual refresh is possible
      }

      return state.userData;
      // try {
      //   const response = await axios.get(
      //     `${process.env.VUE_APP_DEPLOY_URL}/auth/current-user`,
      //     {
      //       withCredentials: true,
      //     }
      //   );

      //   if (response.data.isAuthenticated && response.data.user) {
      //     commit("setUserData", response.data.user);
      //   } else {
      //     commit("clearUserData");
      //   }
      // } catch (error) {
      //   console.error("Error fetching current user:", error);
      //   commit("setError", error.message || "Failed to fetch user data");
      //   commit("clearUserData");
      // } finally {
      //   commit("setLoading", false);
      // }
    },
    async login({ commit, dispatch }, userData) {
      commit("setUserData", userData);
      commit("setAuthChecked", false);

      // Check if the user is new (this information might be in the userData)
      if (userData && userData.isNewUser) {
        console.log(
          "New user detected in login action, setting localStorage flag"
        );
        localStorage.setItem("isNewUser", "true");
      }

      // After login, fetch the complete user data
      await dispatch("fetchCurrentUser", { force: true });
    },
    async logout({ commit }) {
      commit("clearUserData");
      commit("setAuthChecked", false);
    },
  },
  getters: {
    isAuthenticated(state) {
      return state.userID !== null;
    },
    isAuthLoading(state) {
      return state.isLoading;
    },
    currentUser(state) {
      return state.userData;
    },
    userId(state) {
      return state.userID;
    },
    userProfileImage(state) {
      try {
        // Check if user data exists and has identity data
        if (
          state.userData &&
          state.userData.identityData &&
          state.userData.identityData.profilePicture &&
          typeof state.userData.identityData.profilePicture === "string" &&
          state.userData.identityData.profilePicture.trim() !== ""
        ) {
          return state.userData.identityData.profilePicture;
        }
        // If no valid profile picture, return null to trigger the fallback
        return null;
      } catch (error) {
        console.error("Error retrieving user profile image:", error);
        return null;
      }
    },
    userDisplayName(state) {
      if (state.userData) {
        // Try to get display name from various sources
        if (state.userData.identityData?.displayName) {
          return state.userData.identityData.displayName;
        }
        // Fallback to first name + last name
        if (state.userData.identityData?.firstName) {
          const firstName = state.userData.identityData.firstName;
          const lastName = state.userData.identityData?.lastName || "";
          return `${firstName} ${lastName}`.trim();
        }
        // Fallback to email username
        if (state.userData.accountData?.username) {
          return state.userData.accountData.username.split('@')[0];
        }
        // Fallback to email
        if (state.userData.email) {
          return state.userData.email.split('@')[0];
        }
      }
      return "User";
    },
  },
};
